/**
 * Call Quality Optimizer
 * Implements Telnyx recommendations for improved call quality
 * Focuses on bandwidth prioritization and system optimization during calls
 */

interface NetworkInfo {
  effectiveType?: string
  downlink?: number
  rtt?: number
}

interface QualityMetrics {
  jitter: number
  packetLoss: number
  rtt: number
  bandwidth: number
}

class CallQualityOptimizer {
  private isOptimizing = false
  private originalPriorities: Map<string, any> = new Map()
  private performanceObserver: PerformanceObserver | null = null
  private qualityCheckInterval: number | null = null

  /**
   * Start optimizing system for voice calls
   * Implements Telnyx recommendations for network and system optimization
   */
  startOptimization(): void {
    if (this.isOptimizing) return

    console.log('🚀 Starting call quality optimization...')
    this.isOptimizing = true

    // 1. Implement bandwidth prioritization
    this.prioritizeBandwidthForVoice()

    // 2. Optimize browser performance
    this.optimizeBrowserPerformance()

    // 3. Monitor network quality
    this.startNetworkMonitoring()

    // 4. Implement QoS hints
    this.applyQoSHints()

    console.log('✅ Call quality optimization active')
  }

  /**
   * Stop optimization and restore original settings
   */
  stopOptimization(): void {
    if (!this.isOptimizing) return

    console.log('🔄 Stopping call quality optimization...')
    this.isOptimizing = false

    // Restore original settings
    this.restoreOriginalSettings()

    // Stop monitoring
    this.stopNetworkMonitoring()

    console.log('✅ Original system settings restored')
  }

  /**
   * Prioritize bandwidth for voice traffic
   * Based on Telnyx recommendation: "Prioritize voice traffic over other types of data"
   */
  private prioritizeBandwidthForVoice(): void {
    try {
      console.log('🌐 Implementing bandwidth prioritization for voice...')

      // Use Service Worker to throttle non-critical requests during calls
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.ready.then(registration => {
          registration.active?.postMessage({
            type: 'PRIORITIZE_VOICE_TRAFFIC',
            enabled: true
          })
        }).catch(() => {
          console.log('Service Worker not available for traffic prioritization')
        })
      }

      // Reduce background network activity
      this.throttleBackgroundActivity()

      // Apply connection optimizations
      this.optimizeConnectionSettings()

    } catch (error) {
      console.warn('Failed to prioritize bandwidth:', error)
    }
  }

  /**
   * Throttle background network activity during calls
   */
  private throttleBackgroundActivity(): void {
    // Defer non-critical API calls
    this.originalPriorities.set('fetch', window.fetch)
    
    // Wrap fetch to add lower priority for non-voice requests
    window.fetch = (input: RequestInfo | URL, init?: RequestInit) => {
      const url = typeof input === 'string' ? input : input.toString()
      
      // Don't throttle voice/call related endpoints
      if (url.includes('/api/calls/') || url.includes('telnyx') || url.includes('webrtc')) {
        return this.originalPriorities.get('fetch')(input, init)
      }

      // Add delay for non-critical requests during calls
      const enhancedInit = {
        ...init,
        priority: 'low' as RequestPriority
      }

      return new Promise(resolve => {
        setTimeout(() => {
          resolve(this.originalPriorities.get('fetch')(input, enhancedInit))
        }, 100) // Small delay for non-critical requests
      })
    }
  }

  /**
   * Optimize browser performance during calls
   */
  private optimizeBrowserPerformance(): void {
    try {
      console.log('⚡ Optimizing browser performance for voice calls...')

      // Reduce animation frames for non-critical animations
      this.throttleAnimations()

      // Optimize garbage collection
      this.optimizeMemoryUsage()

      // Prioritize audio processing
      this.prioritizeAudioProcessing()

    } catch (error) {
      console.warn('Failed to optimize browser performance:', error)
    }
  }

  /**
   * Apply Quality of Service hints to the browser
   */
  private applyQoSHints(): void {
    try {
      console.log('🎯 Applying QoS hints for voice traffic...')

      // Use Scheduler API if available for task prioritization
      if ('scheduler' in window && (window as any).scheduler.postTask) {
        const scheduler = (window as any).scheduler
        
        // Store original scheduler
        this.originalPriorities.set('scheduler', scheduler.postTask)
        
        // Prioritize audio-related tasks
        scheduler.postTask = (callback: Function, options?: any) => {
          const enhancedOptions = {
            ...options,
            priority: 'user-blocking' // Highest priority for real-time audio
          }
          return this.originalPriorities.get('scheduler')(callback, enhancedOptions)
        }
      }

      // Set network priority hints for voice traffic
      if ('NetworkInformation' in window) {
        const connection = (navigator as any).connection
        if (connection && connection.type) {
          console.log('📶 Network connection type:', connection.type)
          console.log('📊 Network effective type:', connection.effectiveType)
          
          // Adjust quality based on connection
          this.adjustQualityBasedOnConnection(connection)
        }
      }

    } catch (error) {
      console.warn('Failed to apply QoS hints:', error)
    }
  }

  /**
   * Start monitoring network quality during calls
   */
  private startNetworkMonitoring(): void {
    try {
      console.log('📊 Starting network quality monitoring...')

      // Monitor RTT and connection quality
      this.qualityCheckInterval = window.setInterval(() => {
        this.checkNetworkQuality()
      }, 5000) // Check every 5 seconds

      // Use Performance Observer for network timing
      if ('PerformanceObserver' in window) {
        this.performanceObserver = new PerformanceObserver((list) => {
          for (const entry of list.getEntries()) {
            if (entry.entryType === 'navigation') {
              const navEntry = entry as any
              const networkQuality = {
                dns: navEntry.domainLookupEnd - navEntry.domainLookupStart,
                tcp: navEntry.connectEnd - navEntry.connectStart,
                request: navEntry.responseStart - navEntry.requestStart,
                response: navEntry.responseEnd - navEntry.responseStart
              }
              console.log('🌐 Network timing:', networkQuality)
            }
          }
        })

        this.performanceObserver.observe({ entryTypes: ['navigation'] })
      }

    } catch (error) {
      console.warn('Failed to start network monitoring:', error)
    }
  }

  /**
   * Check current network quality and log recommendations
   */
  private checkNetworkQuality(): void {
    if ('connection' in navigator) {
      const connection = (navigator as any).connection
      const quality = {
        effectiveType: connection.effectiveType,
        downlink: connection.downlink,
        rtt: connection.rtt,
        saveData: connection.saveData
      }

      console.log('📊 Current network quality:', quality)

      // Provide recommendations based on network quality
      if (quality.rtt > 150) {
        console.warn('⚠️ High latency detected (RTT: ' + quality.rtt + 'ms). Consider switching to a wired connection.')
      }

      if (quality.downlink < 1) {
        console.warn('⚠️ Low bandwidth detected (' + quality.downlink + ' Mbps). Voice quality may be affected.')
      }

      if (quality.effectiveType === 'slow-2g' || quality.effectiveType === '2g') {
        console.warn('⚠️ Slow connection detected. Consider switching networks for better call quality.')
      }
    }
  }

  /**
   * Adjust call quality settings based on connection type
   */
  private adjustQualityBasedOnConnection(connection: NetworkInfo): void {
    const effectiveType = connection.effectiveType
    const downlink = connection.downlink || 0

    console.log(`🔧 Adjusting quality for ${effectiveType} connection (${downlink} Mbps)`)

    // Recommendations based on connection quality
    if (effectiveType === '4g' && downlink > 5) {
      console.log('✅ Excellent connection - using highest quality settings')
    } else if (effectiveType === '3g' || (effectiveType === '4g' && downlink < 2)) {
      console.log('⚡ Moderate connection - optimizing for stability')
    } else {
      console.log('📶 Limited connection - prioritizing stability over quality')
    }
  }

  /**
   * Throttle animations to free up resources for audio processing
   */
  private throttleAnimations(): void {
    // Store original requestAnimationFrame
    this.originalPriorities.set('requestAnimationFrame', window.requestAnimationFrame)
    
    let animationThrottle = false
    window.requestAnimationFrame = (callback: FrameRequestCallback) => {
      if (animationThrottle) return 0
      
      animationThrottle = true
      setTimeout(() => { animationThrottle = false }, 32) // Throttle to ~30fps
      
      return this.originalPriorities.get('requestAnimationFrame')(callback)
    }
  }

  /**
   * Optimize memory usage during calls
   */
  private optimizeMemoryUsage(): void {
    // Force garbage collection if available (Chrome DevTools)
    if ('gc' in window) {
      (window as any).gc()
      console.log('🗑️ Triggered garbage collection for optimal performance')
    }

    // Clear unnecessary timers and intervals
    this.clearNonCriticalTimers()
  }

  /**
   * Prioritize audio processing threads
   */
  private prioritizeAudioProcessing(): void {
    // Use AudioContext with optimal settings for real-time processing
    if ('AudioContext' in window) {
      const audioContext = new AudioContext({
        latencyHint: 'interactive', // Minimize latency for real-time audio
        sampleRate: 48000 // High sample rate for quality
      })
      console.log('🎵 Optimized AudioContext created for voice processing')
    }
  }

  /**
   * Optimize connection settings for WebRTC
   */
  private optimizeConnectionSettings(): void {
    // These settings would be applied to the WebRTC peer connection
    console.log('🔧 Applying WebRTC connection optimizations...')
    
    // Log recommendations for manual application
    console.log('💡 WebRTC Optimization Recommendations:')
    console.log('   - Use STUN servers: stun:stun.telnyx.com:3478')
    console.log('   - Enable DSCP marking for QoS')
    console.log('   - Set jitter buffer to 100ms for balance')
    console.log('   - Force G.711 PCMU codec for highest quality')
  }

  /**
   * Clear non-critical timers and intervals
   */
  private clearNonCriticalTimers(): void {
    // This would clear any non-essential background tasks
    console.log('⏱️ Optimizing background timers for call performance')
  }

  /**
   * Stop network monitoring
   */
  private stopNetworkMonitoring(): void {
    if (this.qualityCheckInterval) {
      clearInterval(this.qualityCheckInterval)
      this.qualityCheckInterval = null
    }

    if (this.performanceObserver) {
      this.performanceObserver.disconnect()
      this.performanceObserver = null
    }
  }

  /**
   * Restore original browser settings
   */
  private restoreOriginalSettings(): void {
    // Restore original fetch
    if (this.originalPriorities.has('fetch')) {
      window.fetch = this.originalPriorities.get('fetch')
    }

    // Restore original requestAnimationFrame
    if (this.originalPriorities.has('requestAnimationFrame')) {
      window.requestAnimationFrame = this.originalPriorities.get('requestAnimationFrame')
    }

    // Restore scheduler if modified
    if (this.originalPriorities.has('scheduler') && 'scheduler' in window) {
      (window as any).scheduler.postTask = this.originalPriorities.get('scheduler')
    }

    // Notify service worker to stop prioritization
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.ready.then(registration => {
        registration.active?.postMessage({
          type: 'PRIORITIZE_VOICE_TRAFFIC',
          enabled: false
        })
      }).catch(() => {
        console.log('Service Worker not available')
      })
    }

    this.originalPriorities.clear()
  }

  /**
   * Get current optimization status
   */
  isActive(): boolean {
    return this.isOptimizing
  }

  /**
   * Get network quality recommendations
   */
  getNetworkRecommendations(): string[] {
    const recommendations = [
      '📶 Use a wired connection instead of Wi-Fi when possible',
      '🌐 Ensure sufficient bandwidth (minimum 100kbps up/down for voice)',
      '⚡ Close bandwidth-heavy applications (streaming, downloads)',
      '🔧 Enable QoS on your router to prioritize VoIP traffic',
      '📍 Position closer to your Wi-Fi router if using wireless',
      '🔄 Restart your router if experiencing connection issues',
      '📞 Use a high-quality headset with noise cancellation',
      '🎙️ Speak clearly and avoid background noise'
    ]
    return recommendations
  }
}

// Export singleton instance
export const callQualityOptimizer = new CallQualityOptimizer()

// Type definitions for better TypeScript support
declare global {
  interface Window {
    gc?: () => void
  }
}

export type { NetworkInfo, QualityMetrics } 